import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, NavigationError, NavigationCancel, Router } from '@angular/router';
import {
  MsalBroadcastService,
  MsalService
} from '@azure/msal-angular';
import {
  AuthError,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  Logger,
  LogLevel
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ClaimsService } from '@core/authentication/claims.service';
import { HotjarService } from '@core/services/hotjar.service';
import { MonitoringService } from '@core/services/monitoring.service';
import { AssignableUsersService } from '@core/services/assignable-users.service';
import { environment } from '@env/environment';
import { AppStatusService } from '@shared/services/service-workers';
import { AuthenticationService } from '@core/authentication/auth.service';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { loginRequest } from './auth-config';
import { HeapAnalyticsService } from '@shared/services/heap-analytics.service';
import { B2C_ERROR_CODES, isB2CError } from '@core/constants/b2c-error-codes';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  public isIframe: boolean = false;
  public isNavigating: boolean = false;

  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalAuthService: MsalService,
    public claimsService: ClaimsService,
    public appStatus: AppStatusService,
    private hotjarService: HotjarService,
    private router: Router,
    private monitoringService: MonitoringService,
    private authService: AuthenticationService,
    private heapAnalytics: HeapAnalyticsService,
    private assignableUsersService: AssignableUsersService
  ) {
    // load analytics services
    this.hotjarService.setConfig(environment.hotjarId);
    // heapAnalytics automatically initializes when injected
  }

  public isAuthenticated: boolean = false;
  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.checkPendingNewVersion();
    this.checkAuthInteractions();
    this.setAuthLogger();
    this.syncBrowserEvents();   
    this.subscribeToFailures();
    this.clearCacheOnTokenNetworkRefresh();
    this.trackPageViews();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  } 

  private setAuthLogger(): void {
    this.msalAuthService.setLogger(
      new Logger({
        loggerCallback: (logLevel, message, piiEnabled) => {
          switch (logLevel) {
            case LogLevel.Error:
              this.monitoringService.logTrace(message, {}, SeverityLevel.Information);
              break;
            default:
              // No need to log every interaction.  Can be used for debug purpose.
              // this.monitoringService.logTrace(message);
              break;
          }
        },
        piiLoggingEnabled: false,
        logLevel: LogLevel.Error
      })
    );
  }

  private syncBrowserEvents(): void {
    this.msalAuthService.instance.enableAccountStorageEvents(); //Enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((msg: EventMessage) => {
        if (this.authService.getB2CAccounts().length === 0) {
          // Clear cached user data on logout
          this.assignableUsersService.clearCache();
          window.location.pathname = '/';
        }
        
        // Also clear cache on account change (different user logged in)
        if (msg.eventType === EventType.ACCOUNT_ADDED) {
          this.assignableUsersService.clearCache();
        }
      });
  }

  /**
   * Clear assignable users cache when authentication token is refreshed from the network.
   * This ensures users get fresh data whenever MSAL makes a network call to refresh the token.
   * 
   * ACQUIRE_TOKEN_NETWORK_START only fires when MSAL actually contacts Azure AD B2C for a new token,
   * not when returning cached tokens, so this happens ~every 50-60 minutes (based on token lifetime).
   */
  private clearCacheOnTokenNetworkRefresh(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => 
            msg.eventType === EventType.ACQUIRE_TOKEN_NETWORK_START
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.assignableUsersService.clearCache();
        this.monitoringService.logTrace(
          'Cleared assignable users cache on token network refresh',
          {},
          SeverityLevel.Information
        );
      });
  }

  private subscribeToFailures(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(        
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE || msg.eventType === EventType.LOGIN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const error = result.error as InteractionRequiredAuthError; 
        
        // User cancelled password change or self-asserted flow
        if (error && isB2CError(error.errorMessage, B2C_ERROR_CODES.USER_CANCELLED)) {
          this.monitoringService.logTrace("B2C user flow cancelled (AADB2C90091), retrying login", { error });
          this.msalAuthService.instance.loginRedirect(loginRequest);
          return;
        }
        
        // Handle other authentication errors
        if (result.error != null) {
          this.logError(result.error as AuthError);
          
          // For specific error types, clear cache and force re-authentication
          // This handles cases where cached tokens are corrupted or invalid
          if (this.shouldClearCacheForError(error)) {
            this.monitoringService.logTrace("Clearing cached auth state due to authentication error", { 
              errorCode: error?.errorCode,
              errorMessage: error?.errorMessage 
            });
            
            // Selectively clear only MSAL-related keys from localStorage and sessionStorage
            this.authService.clearMsalStorage();
          }
        }
      });
  }

  /**
   * Determine if cache should be cleared for specific error types
   */
  private shouldClearCacheForError(error: InteractionRequiredAuthError): boolean {
    if (!error) return false;
    
    // Clear cache for these specific B2C error codes
    const errorCodesThatRequireCacheClear = [
      'invalid_grant',           // Token is invalid or expired
      'interaction_required',    // User interaction is required
      'consent_required',        // User consent is required
      'login_required',          // User must login again
      'token_expired'            // Token has expired
    ];
    
    return errorCodesThatRequireCacheClear.some(code => 
      error.errorCode?.includes(code) || error.errorMessage?.includes(code)
    );
  }

  private logError(error: AuthError){
    if (error.name == 'InteractionRequiredAuthError') {
      this.monitoringService.logTrace("Interaction required MSAL error", { error }, SeverityLevel.Information);
    }
    else{
      this.monitoringService.logException(error);
    }
  }

  private checkAuthInteractions(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.authService.checkAndSetActiveAccount();
        this.identifyUserInHeap();
      });
  }  

  private identifyUserInHeap(): void {
    if (this.authService.isAuthenticated && this.claimsService.portfolioId) {
      this.heapAnalytics.identify(this.claimsService.portfolioId);
    }
  }

  private trackPageViews(): void {
    // Track navigation start and show loading indicator
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.isNavigating = true;
        // Note: document.title will be updated by PageTitleStrategy during navigation
        this.monitoringService.startTrackPageView(document.title);
      });

    // Track navigation end with duration and hide loading indicator
    // Note: This tracks when routing completes, NOT when the page is fully rendered
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe((event: NavigationEnd) => {
        this.isNavigating = false;
        const fullUrl = window.location.origin + event.url;
        
        // Title has been updated by PageTitleStrategy at this point
        this.monitoringService.stopTrackPageView(document.title, fullUrl);
      });

    // Also hide loading indicator on navigation error or cancel
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationError || e instanceof NavigationCancel),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.isNavigating = false;
      });
  }

  private checkPendingNewVersion(): void {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.appStatus.hasAvailableUpdate) {
          window.location.reload();
        }
      });
  }
}
